import clsx from "clsx";
import { ClickableCard } from "components";
import FileHelper, { FileValidationError } from "helpers/FileHelper";
import { useCallback, useMemo, useRef, useState } from "react";
import { StorageService } from "services/storage";
import { RegularState, SuccessState, UploadErrorState } from "./states";
import "./styles.scss";
import { FileInputProps, InnerStateProps } from "./types";

const baseClassName = "file-input-component flex flex-col gap-2 justify-center";

function getInnerStateComponent(conditions: {
	isUploading: boolean;
	hasUploadError: boolean;
	hasValue: boolean;
}): (props: Readonly<FileInputProps & InnerStateProps>) => JSX.Element {
	const { isUploading, hasUploadError, hasValue } = conditions;
	if (isUploading) return RegularState;
	if (hasUploadError) return UploadErrorState;
	if (hasValue) return SuccessState;
	return RegularState;
}

export default function FileInput(props: FileInputProps) {
	const {
		termId,
		placeholder,
		name,
		description,
		required = false,
		disabled,
		error,
		touched,
		onAttachmentChange,
		className = "",
		value,
		acceptedFormats,
		maxByteSize,
		setFieldValue
	} = props;
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [fileName, setFileName] = useState<string | null>(value || null);
	const [isUploading, setIsUploading] = useState<boolean>(false);
	const [uploadProgress, setUploadProgress] = useState<number>(0);
	const [uploadError, setUploadError] = useState<boolean>(false);
	const [validationError, setValidationError] = useState<string>("");

	const uploadFile = useCallback(async (): Promise<void> => {
		const target = inputRef.current;
		if (target === null) throw new Error("No input element");

		const { files } = target;
		const file = files && files.length > 0 ? files[0] : null;

		setUploadError(false);
		setUploadProgress(0);
		setValidationError("");

		if (file === null) return;

		setIsUploading(true);
		setFileName(file.name);

		try {
			await FileHelper.validateFile(file, acceptedFormats, maxByteSize);
		} catch (validateFileError) {
			if (validateFileError instanceof FileValidationError)
				setValidationError(validateFileError.message);
			setIsUploading(false);
			return;
		}

		StorageService.uploadFile(
			file,
			(percent: number) => {
				setUploadProgress(percent);
			},
			termId
		)
			.then((response) => {
				const formattedFileName = FileHelper.getFileNameFromUrl(response) ?? "";
				setFieldValue(name, response);
				setFileName(formattedFileName);
				onAttachmentChange(response);
			})
			.catch((errorUpload) => {
				if (errorUpload instanceof FileValidationError)
					setValidationError(errorUpload.message);
				else setUploadError(true);
			})
			.finally(() => {
				setIsUploading(false);
			});
	}, [name, acceptedFormats, maxByteSize, onAttachmentChange]);

	const valueErrors = useMemo(() => {
		let errorList = [];
		if (validationError) {
			errorList.push(validationError);
		}
		if (error) {
			if (Array.isArray(error)) {
				errorList = [...errorList, ...error];
			} else {
				errorList.push(error);
			}
		}
		return errorList;
	}, [validationError, error]);

	const hasValueError = Boolean(
		valueErrors.length && (!!touched || validationError)
	);

	const hasError = Boolean(uploadError || hasValueError);

	const hasValue = (): boolean => {
		return !!value;
	};

	const InnerState = getInnerStateComponent({
		isUploading,
		hasUploadError: uploadError,
		hasValue: hasValue()
	});

	return (
		<div className={`container ${className}`}>
			<ClickableCard
				onClick={() => inputRef.current?.click()}
				className={clsx(baseClassName, {
					error: hasError,
					sucess: !hasError && value
				})}
			>
				<input
					ref={inputRef}
					type="file"
					name={name}
					placeholder={placeholder}
					onChange={() => uploadFile()}
					disabled={disabled ?? isUploading}
					required={required}
					hidden
				/>
				<InnerState
					{...props}
					fileName={fileName || ""}
					uploadFile={uploadFile}
					isUploading={isUploading}
					setIsUploading={setIsUploading}
					uploadError={uploadError}
					setUploadError={setUploadError}
					error={error}
					touched={touched}
					uploadProgress={uploadProgress}
					hasValueError={hasValueError}
				/>
			</ClickableCard>
			{hasError && Boolean(valueErrors.length) && (
				<div className="white-space--pre-line mt-1 text-xs">
					{valueErrors.join("\n")}
				</div>
			)}
			{description && (
				<p className="text-neutral-low-400 text-sm pt-2">{description}</p>
			)}
		</div>
	);
}
